import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { UNAUTHORIZED_EVENT } from '@/helpers/nate_fetch';
import useVpnStore from '@/stores/vpn';

// Fallback used for the login redirect if a region base URL isn't resolved yet
// (e.g. the selected proxy was briefly unreachable). The session cookie is scoped
// to the parent domain, so signing in via either region works for both.
const FALLBACK_PROXY_BASE_URL = import.meta.env.VITE_NATE_EU_PROXY_URL || 'https://proxy.eu.robocam.slideslive.dev';

const useAuthStore = defineStore('auth', () => {
  const vpnStore = useVpnStore();

  // Auth only applies to the public (HTTPS) deployment. Over the VPN the app is
  // served via nginx over http and the proxy skips auth by source IP, so there's
  // no sign-in at all.
  const isPublic = computed(() => typeof window !== 'undefined' && window.location.protocol === 'https:');

  const checked = ref(false);
  const authenticated = ref(false);
  const user = ref(null);

  // The proxy is a same-site host, so all requests carry the HttpOnly session cookie.
  const authBaseUrl = () => vpnStore.activeBaseUrl || FALLBACK_PROXY_BASE_URL;

  async function check() {
    if (!isPublic.value) {
      authenticated.value = true;
      checked.value = true;
      return;
    }

    try {
      const response = await fetch(`${authBaseUrl()}/auth/me`, { credentials: 'include' });
      const data = await response.json();

      authenticated.value = data.authenticated === true;
      user.value = authenticated.value ? { email: data.email, name: data.name } : null;
    } catch (error) {
      console.warn('Auth check failed:', error);
      authenticated.value = false;
      user.value = null;
    } finally {
      checked.value = true;
    }
  }

  function signIn() {
    const returnTo = encodeURIComponent(window.location.href);
    window.location.assign(`${authBaseUrl()}/auth/login?return_to=${returnTo}`);
  }

  async function signOut() {
    try {
      await fetch(`${authBaseUrl()}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.warn('Sign out request failed:', error);
    }

    authenticated.value = false;
    user.value = null;
    window.location.reload();
  }

  // Called when a backend request returns 401 (session expired). Flips the gate back
  // to the sign-in view without a reload; the interval polling will simply keep failing.
  function handleUnauthorized() {
    if (!isPublic.value) return;

    authenticated.value = false;
    user.value = null;
  }

  // nateFetch dispatches this on any 401 (kept as an event to avoid a store import cycle).
  if (typeof window !== 'undefined') {
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }

  return {
    isPublic,
    checked,
    authenticated,
    user,

    check,
    signIn,
    signOut,
    handleUnauthorized,
  };
});

export default useAuthStore;
